import { FeedbackModal } from "./FeedbackModal"
import { ThankYouAlert } from "./ThankYouAlert"
import { fetchFeedback } from "./api"
import LogoIcon from "@/assets/icons/logo.svg"
import ThumbUpIcon from "@/assets/icons/thumb-up.png"
import { ERROR_MESSAGE, MOBILE_REGEXP } from "@/constants"
import type { Message, Reaction } from "@/types"
import { formatTime } from "@/utils/formatDates"
import { type Dispatch, type SetStateAction, memo, useRef, useState } from "react"
import ReactMarkdown from "react-markdown"
import rehypeRaw from "rehype-raw"
import remarkGfm from "remark-gfm"

interface Props {
  message: Message
  setMessages: Dispatch<SetStateAction<Message[]>>
  idx: number
  sessionId: string
}

export const MessageItem = memo((props: Props) => {
  const { message, setMessages, idx, sessionId } = props

  const isBot = message.sender === "bot"
  const isError = message.text === ERROR_MESSAGE

  const clickLikeHandler = () => {
    if (message.reaction === "like") return

    sendFeedback("like")
  }

  const [showFeedbackModal, setShowFeedbackModal] = useState(false)

  const clickDislikeHandler = () => {
    if (message.reaction === "dislike") return

    setShowFeedbackModal(true)
  }

  const sendFeedback = (reaction: NonNullable<Reaction>, text: string = "") => {
    try {
      fetchFeedback(reaction, sessionId, message.id, text)
    } catch (error) {
      console.error("Ошибка отправки отзыва: ", error)
    } finally {
      setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, reaction } : m)))

      if (reaction === "dislike") {
        setShowFeedbackModal(false)

        setShowThankYouAlert(true)
        thankYouAlertId.current = setTimeout(() => setShowThankYouAlert(false), 3000)
      }
    }
  }

  const [showThankYouAlert, setShowThankYouAlert] = useState(false)
  const thankYouAlertId = useRef<number | null>(null)

  const closeThankYouAlert = () => {
    setShowThankYouAlert(false)
    if (thankYouAlertId.current) clearTimeout(thankYouAlertId.current)
    thankYouAlertId.current = null
  }

  const [showReactions, setShowReactions] = useState(false)
  const handleClick = () => {
    if (MOBILE_REGEXP.test(navigator.userAgent)) {
      setShowReactions((prev) => !prev)
    }
  }

  return (
    <>
      <div
        key={message.id}
        data-date={message.date}
        className={`w-fit max-w-[91%] flex flex-col ${isBot ? "items-start gap-2 relative pl-8" : "!ml-auto items-end"} hover:[&>.reactions]:opacity-100 hover:[&>.reactions]:visible focus:[&>.reactions]:opacity-100 focus:[&>.reactions]:visible`}
        onClick={handleClick}
      >
        {isBot && (
          <>
            <LogoIcon className="absolute left-0 top-0 w-6 h-6" />
            <div className="text-xs tracking-[0.02em] text-[#8c8c8c]">AI-ассистент</div>
          </>
        )}
        <div className={`p-3 rounded-2xl text-[#ffffff] flex flex-col space-y-2 ${isBot ? "bg-[#1f1f1f] items-start" : "bg-gradient items-end"}`}>
          <div className="mrkdown-wrapper">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              components={{ a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" /> }}
            >
              {message.text}
            </ReactMarkdown>
          </div>
          <span className={`text-xs tracking-[0.02em] !ml-auto ${isBot ? "text-[#8c8c8c]" : ""}`}>{formatTime(message.date)}</span>
        </div>

        {!isError && isBot && idx !== 0 && (
          <div
            className={`reactions flex items-center gap-1 transition duration-300 ${showReactions ? "opacity-100 visible" : "opacity-0 invisible"}`}
          >
            <button
              onClick={clickLikeHandler}
              className={`reaction-btn ${message.reaction === "like" ? "border-[#f28425]! pointer-events-none" : ""}`}
            >
              <img src={ThumbUpIcon} alt="Палец вверх" />
            </button>
            <button
              onClick={clickDislikeHandler}
              className={`reaction-btn ${message.reaction === "dislike" ? "border-[#f28425]! pointer-events-none" : ""}`}
            >
              <img src={ThumbUpIcon} className="rotate-180" alt="Палец вниз" />
            </button>
          </div>
        )}
      </div>

      <FeedbackModal open={showFeedbackModal} onClose={setShowFeedbackModal} sendFeedback={(text) => sendFeedback("dislike", text)} />

      <ThankYouAlert open={showThankYouAlert} onClose={closeThankYouAlert} />
    </>
  )
})
